// The top and bottom pagers: each is the Buttons Pagination. While rows
// load or fail, both pagers stay in place, focusable and blocked, through its
// busy and blocked inputs. They share the page through the table state, so
// moving one moves the other.

import React, { useRef } from 'react'
import PropTypes from 'prop-types'

import { Pagination } from '@CommonComponents/buttons'
import { useTable } from '../context'
import { ACTIONS } from '../types'
import { pageScopedSelection, pagerModel } from './model'

const LABELS = { top: 'Pagination, top', bottom: 'Pagination, bottom' }

const CLEARED_SELECTION = () => ({
  scope: 'none',
  ids: new Set(),
  excluded: new Set(),
})

/** The page-scoped selection ends with its page; reason names the cause. */
export const useClearPageSelection = () => {
  const { state, dispatch } = useTable()
  return reason => {
    if (pageScopedSelection(state.selection))
      dispatch({
        type: ACTIONS.selectionSet,
        selection: CLEARED_SELECTION(),
        reason,
      })
  }
}

const usePagerModel = () => {
  const { status, state, derived } = useTable()
  const knownTotal = useRef(0)
  if (status === 'ready') knownTotal.current = derived.total
  return pagerModel({
    status,
    page: state.page,
    currentPage: derived.currentPage,
    total: derived.total,
    pageSize: state.pageSize,
    knownTotal: knownTotal.current,
  })
}

export const Pager = ({ position }) => {
  const { state, dispatch, derived } = useTable()
  const { pages, current, blocked, busy } = usePagerModel()
  const clearPageSelection = useClearPageSelection()

  if (derived.zeroData) return null

  return (
    <Pagination
      // density reaches only the table's rows, cells and headers; the pager keeps one size
      label={LABELS[position]}
      currentPage={current}
      rate={state.pageSize}
      // the model's page count, which outlives a load in flight or failed
      total={pages * state.pageSize}
      busy={busy}
      blocked={blocked}
      setCurrentPage={page => {
        dispatch({ type: ACTIONS.pageSet, page, reason: 'page' })
        clearPageSelection('page')
      }}
    />
  )
}

Pager.propTypes = {
  position: PropTypes.oneOf(['top', 'bottom']).isRequired,
}

export const TopPager = () => <Pager position="top" />
